import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { productsAPI } from '../services/api'
import { ArrowLeft, ShoppingCart, Leaf, CheckCircle, Package, Truck } from 'lucide-react'
import { getImageUrl } from '../utils/imageHelper'

const ProductDetail = () => {
  const { id } = useParams()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedSize, setSelectedSize] = useState(0)

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true)
        const response = await productsAPI.getById(id)
        // Handle both old format (response.data.data) and new format (response.data)
        const product = response.data?.id ? response.data : response.data?.data;
        setProduct(product)
        setSelectedSize(0) // Select first size by default
      } catch (err) {
        console.error('Error fetching product:', err)
        setError('Product not found or failed to load.')
      } finally {
        setLoading(false)
      }
    }

    fetchProduct()
  }, [id])

  const handleBuyClick = () => {
    const selectedProduct = {
      ...product,
      selectedSize: product.sizes[selectedSize]
    }
    alert(`Added ${product.name} (${product.sizes[selectedSize].weight}) to cart! (This is a demo)`)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-earth-green"></div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link to="/products" className="btn-primary">
            Back to Products
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b">
        <div className="container-custom">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-earth-green">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/products" className="text-gray-600 hover:text-earth-green">Products</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="aspect-square rounded-xl overflow-hidden bg-white shadow-lg">
              <img
                src={getImageUrl(product.image)}
                alt={product.name}
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
            <div className="flex space-x-2">
              <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-earth-green">
                <img
                  src={getImageUrl(product.image)}
                  alt={product.name}
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <span className="bg-earth-green text-white px-3 py-1 rounded-full text-sm font-medium capitalize">
                  {product.category}
                </span>
                <div className="flex items-center text-olive">
                  <Leaf className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">Premium Quality</span>
                </div>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
              <p className="text-xl text-gray-600">{product.shortDesc}</p>
            </div>

            {/* NPK Values */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Nutrient Composition</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-earth-green mb-1">N</div>
                  <div className="text-2xl font-semibold">{product.npk.n}%</div>
                  <div className="text-sm text-gray-600">Nitrogen</div>
                </div>
                <div className="text-center p-4 bg-olive bg-opacity-10 rounded-lg">
                  <div className="text-3xl font-bold text-olive mb-1">P</div>
                  <div className="text-2xl font-semibold">{product.npk.p}%</div>
                  <div className="text-sm text-gray-600">Phosphorus</div>
                </div>
                <div className="text-center p-4 bg-brand-blue bg-opacity-10 rounded-lg">
                  <div className="text-3xl font-bold text-brand-blue mb-1">K</div>
                  <div className="text-2xl font-semibold">{product.npk.k}%</div>
                  <div className="text-sm text-gray-600">Potassium</div>
                </div>
              </div>
            </div>

            {/* Micronutrients */}
            {product.micros && product.micros.length > 0 && (
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Micronutrients</h3>
                <div className="grid grid-cols-2 gap-3">
                  {product.micros.map((micro, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">{micro.name}</span>
                      <span className="text-earth-green font-semibold">{micro.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Available Sizes</h3>
              <div className="grid grid-cols-1 gap-3">
                {product.sizes.map((size, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSize(index)}
                    className={`p-4 rounded-lg border-2 transition-all ${selectedSize === index
                      ? 'border-earth-green bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold">{size.weight}</div>
                        <div className="text-sm text-gray-600">Package size</div>
                      </div>
                      <div className="text-xl font-bold text-brand-blue">
                        ${size.price}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Price and Buy Button */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-3xl font-bold text-brand-blue">
                    ${product.sizes[selectedSize].price}
                  </div>
                  <div className="text-gray-600">
                    for {product.sizes[selectedSize].weight}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Free shipping on orders over $100</div>
                </div>
              </div>
              <button
                onClick={handleBuyClick}
                className="w-full btn-primary flex items-center justify-center group text-lg py-4"
              >
                <ShoppingCart className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                Add to Cart
              </button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center space-x-3 p-4 bg-white rounded-lg shadow">
                <CheckCircle className="h-6 w-6 text-brand-blue" />
                <span className="text-gray-700">Premium quality guaranteed</span>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-white rounded-lg shadow">
                <Package className="h-6 w-6 text-brand-blue" />
                <span className="text-gray-700">Secure packaging</span>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-white rounded-lg shadow">
                <Truck className="h-6 w-6 text-brand-blue" />
                <span className="text-gray-700">Fast delivery</span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Description */}
        <div className="mt-16 bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Product Description</h2>
          <div className="prose max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {product.fullDesc}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Usage Instructions</h3>
            <p className="text-gray-700 leading-relaxed">
              {product.usageInstructions}
            </p>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <Link
            to="/products"
            className="btn-outline inline-flex items-center group"
          >
            <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Products
          </Link>
        </div>
      </div>
    </div>
  )
}

export default ProductDetail
